import { useState, useEffect, useRef } from 'react';
import {
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
} from 'date-fns';

import { Button } from '../Button/Index';
import {
  IconBell,
  IconBellActive,
  IconTask,
  IconHistory,
  IconMenu,
} from '../Icon/Index';
import IconNotificationEmpty from './icon-navbar-notification-empty.svg?react';
import IconSidebar from './icon-sidebar.svg?react';
import { NotificationItemType } from './types';

import styles from './Navbar.module.scss';

type NavbarType = {
  quickLinksComponent: React.ReactNode;
  userMenu: {
    fullname: string;
    avatarImage?: string;
    links: React.ReactNode[];
  };
  unifiedLibraryHomepageLink: string;
  showNotificationIcon?: boolean;
  hasActiveNotification?: boolean;
  notifications?: NotificationItemType[];
  toggleSidebar?: () => void;
  showQuickLinksIcon?: boolean;
  showTaskIcon?: boolean;
  onTaskIconClick?: () => void;
  showHistoryIcon?: boolean;
  onHistoryIconClick?: () => void;
};

const getInitials = (label: string) => {
  return label
    .split(' ')
    .map((word: string) => (word[0] !== undefined ? word[0].toUpperCase() : ''))
    .join('');
};

const formatDateFromNow = (pastDate: Date) => {
  const now = new Date();
  const seconds = differenceInSeconds(now, pastDate);
  const minutes = differenceInMinutes(now, pastDate);
  const hours = differenceInHours(now, pastDate);
  const days = differenceInDays(now, pastDate);

  if (seconds < 60) {
    return `Just Now`;
  } else if (minutes < 60) {
    return `${Math.floor(minutes)} minute${minutes === 1 ? '' : 's'} ago`;
  } else if (hours < 24) {
    return `${Math.floor(hours)} hour${hours === 1 ? '' : 's'} ago`;
  } else {
    return `${days.toFixed(0)} day${days === 1 ? '' : 's'} ago`;
  }
};

const Navbar = (props: NavbarType) => {
  const {
    quickLinksComponent,
    userMenu,
    unifiedLibraryHomepageLink,
    showNotificationIcon = true,
    hasActiveNotification = false,
    notifications = [],
    toggleSidebar = null,
    showQuickLinksIcon = true,
    showTaskIcon = true,
    onTaskIconClick = null,
    showHistoryIcon = true,
    onHistoryIconClick = null,
  } = props;
  const [dropdownUserVisible, setDropdownUserVisible] =
    useState<boolean>(false);
  const [dropdownMenuVisible, setDropdownMenuVisible] =
    useState<boolean>(false);
  const [dropdownNotificationVisible, setDropdownNotificationVisible] =
    useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Element)) {
      setDropdownMenuVisible(false);
      setDropdownUserVisible(false);
    }
  };

  const NotificationsNavbar = () => {
    return (
      <>
        {showNotificationIcon && (
          <button
            type='button'
            onClick={() => {
              setDropdownNotificationVisible(!dropdownNotificationVisible);
              setDropdownMenuVisible(false);
              setDropdownUserVisible(false);
            }}
          >
            {hasActiveNotification ? (
              <IconBellActive fill='#fff' className='theme-navbar-icons' />
            ) : (
              <IconBell fill='#fff' className='theme-navbar-icons' />
            )}
          </button>
        )}

        <div
          className={`theme-navbar-dropdown theme-navbar-notif-dropdown ${
            dropdownNotificationVisible && 'dropdown-visible'
          }`}
        >
          {hasActiveNotification && notifications.length > 0 && (
            <ul>
              {notifications.map(
                (notification: NotificationItemType, index: number) => {
                  return RenderNotification(notification, index);
                },
              )}
            </ul>
          )}
          {(!hasActiveNotification || notifications.length <= 0) && (
            <div className='theme-navbar-notif-empty-wrapper'>
              <IconNotificationEmpty />
              <p className='title'>No notification yet</p>
              <p>Any notification you receive will show up here</p>
            </div>
          )}
        </div>
      </>
    );
  };

  const RenderNotification = (item: NotificationItemType, index: number) => {
    return (
      <li
        key={`notification-${index}`}
        className={`${item.hasRead && 'theme-navbar-notif-has-read'}`}
        onClick={() => item.onClick && item.onClick()}
      >
        {(item.sourceNames === undefined || item.sourceNames.length <= 0) && (
          <div className='theme-navbar-notif-icon icon-default'>
            <IconBell fill='#3548C9' />
          </div>
        )}
        {item.sourceNames &&
          Array.isArray(item.sourceNames) &&
          item.sourceNames[0] !== undefined &&
          item.sourceNames.length === 1 && (
            <div className='theme-navbar-notif-icon icon-user'>
              <span className='icon-name'>
                {getInitials(item.sourceNames[0])}
              </span>
            </div>
          )}
        {item.sourceNames &&
          Array.isArray(item.sourceNames) &&
          item.sourceNames.length > 1 && (
            <div className='theme-navbar-notif-icon-stack'>
              {item.sourceNames.map((name: string, sourceNameIndex: number) => {
                return (
                  <div
                    className='theme-navbar-notif-icon icon-user'
                    key={`notif-${index}-${sourceNameIndex}`}
                  >
                    <span className='icon-name'>{getInitials(name)}</span>
                  </div>
                );
              })}
            </div>
          )}
        <div className='theme-navbar-notif-content'>
          <p>{item.title}</p>
          <p dangerouslySetInnerHTML={{ __html: item.description }} />
          <small>{formatDateFromNow(item.time)}</small>
        </div>
        {!item.hasRead && <span className='theme-navbar-notif-readicon'></span>}
      </li>
    );
  };

  const quicklinksDropdownButtonRef = useRef<HTMLButtonElement>(null);
  const quicklinksDropdownRef = useRef<HTMLDivElement>(null);

  const QuickLinksNavbar = () => {
    return (
      <>
        <button
          type='button'
          ref={quicklinksDropdownButtonRef}
          onClick={() => {
            setDropdownMenuVisible(!dropdownMenuVisible);
            setDropdownNotificationVisible(false);
            setDropdownUserVisible(false);
          }}
        >
          <IconMenu fill='#fff' className='theme-navbar-icons' />
        </button>

        <div
          ref={quicklinksDropdownRef}
          className={`theme-navbar-dropdown theme-navbar-quicklinks-dropdown ${
            dropdownMenuVisible && 'dropdown-visible'
          }`}
        >
          {quickLinksComponent}
          <Button
            className={styles.homepageButton}
            variant='outline'
            label='Go To Homepage'
            onClick={e => {
              e.preventDefault();
              window.open(unifiedLibraryHomepageLink, '_self');
            }}
          />
        </div>
      </>
    );
  };

  useEffect(() => {
    const handleResize = () => {
      if (
        dropdownMenuVisible &&
        quicklinksDropdownRef.current &&
        quicklinksDropdownButtonRef.current
      ) {
        const buttonRect =
          quicklinksDropdownButtonRef.current.getBoundingClientRect();
        const dropdownRect =
          quicklinksDropdownRef.current.getBoundingClientRect();
        const left = buttonRect.left + buttonRect.width - dropdownRect.width;
        quicklinksDropdownRef.current.style.left = `${left}px`;
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dropdownMenuVisible]);

  const UserNavbar = () => {
    return (
      <>
        <button
          type='button'
          onClick={() => {
            setDropdownUserVisible(!dropdownUserVisible);
            setDropdownNotificationVisible(false);
            setDropdownMenuVisible(false);
          }}
        >
          <span className='theme-navbar-avatar'>
            {getInitials(userMenu.fullname)}
          </span>
          <span className='theme-navbar-avatar-name'>{userMenu.fullname}</span>
        </button>

        <div
          className={`theme-navbar-dropdown theme-navbar-user-dropdown ${
            dropdownUserVisible && 'dropdown-visible'
          }`}
          id='user-dropdown'
        >
          <ul>
            {userMenu.links.map((link: React.ReactNode, index: number) => {
              return <li key={`user-dropdown-${index}`}>{link}</li>;
            })}
          </ul>
        </div>
      </>
    );
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <nav className='theme-navbar' ref={ref}>
      <div className='theme-navbar-wrapper'>
        <div className='theme-navbar-sidebar-wrapper'>
          <button
            type='button'
            className='theme-navbar-sidebar-button'
            onClick={() => {
              toggleSidebar && toggleSidebar();
              setDropdownMenuVisible(false);
              setDropdownMenuVisible(false);
            }}
          >
            <IconSidebar />
          </button>
        </div>

        <div className='theme-navbar-buttons'>
          {NotificationsNavbar()}
          {showTaskIcon && (
            <button
              type='button'
              onClick={() => {
                onTaskIconClick && onTaskIconClick();
              }}
            >
              <IconTask fill='#fff' className='theme-navbar-icons' />
            </button>
          )}
          {showHistoryIcon && (
            <button
              type='button'
              onClick={() => {
                onHistoryIconClick && onHistoryIconClick();
              }}
            >
              <IconHistory fill='#fff' className='theme-navbar-icons' />
            </button>
          )}
          {showQuickLinksIcon && QuickLinksNavbar()}
          {UserNavbar()}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
